import { Localized } from "@fluent/react/compat";
import React, { FunctionComponent, useCallback } from "react";
import { useField } from "react-final-form";
import { graphql } from "react-relay";

import {
  FieldSet,
  FormField,
  FormFieldDescription,
  FormFieldHeader,
  HorizontalGutter,
  Label,
} from "coral-ui/components/v2";

import SiteSearch from "coral-admin/components/SiteSearch";
import ConfigBox from "../../ConfigBox";
import Header from "../../Header";
import HelperText from "../../HelperText";
import OnOffField from "../../OnOffField";
import SpecificSitesSelectedQuery from "./SpecificSitesSelectedQuery";

// eslint-disable-next-line no-unused-expressions
graphql`
  fragment PinCommentConfig_formValues on Settings {
    enableQaFlag
    qaFlagEnabledSites
  }
`;

interface Props {
  disabled: boolean;
}

const PinCommentsConfig: FunctionComponent<Props> = ({ disabled }) => {
  const { input: enableQaFlagInput } = useField<boolean>("enableQaFlag");
  const { input: qaFlagEnabledSitesInput } = useField<string[]>(
    "qaFlagEnabledSites"
  );

  const onAddSite = useCallback(
    (siteID: string) => {
      const currentValue = qaFlagEnabledSitesInput.value || [];
      const changed = [...currentValue];
      const index = changed.indexOf(siteID);
      if (index === -1) {
        changed.push(siteID);
      }
      qaFlagEnabledSitesInput.onChange(changed);
    },
    [qaFlagEnabledSitesInput]
  );

  const onRemoveSite = useCallback(
    (siteID: string) => {
      const currentValue = qaFlagEnabledSitesInput.value || [];
      const changed = [...currentValue];
      const index = changed.indexOf(siteID);
      if (index >= 0) {
        changed.splice(index, 1);
      }
      qaFlagEnabledSitesInput.onChange(changed);
    },
    [qaFlagEnabledSitesInput]
  );

  return (
    <ConfigBox
      title={
        <Localized id="configure-moderation-pin-comment-title">
          <Header container={<legend />}>Pin Comments</Header>
        </Localized>
      }
      container={<FieldSet />}
    >
      <Localized id="configure-moderation-pin-comments-description">
        <FormFieldDescription>
          Configure pin comment functionality.
        </FormFieldDescription>
      </Localized>
      <FormField container={<FieldSet />}>
        <FormFieldHeader>
          <Localized id="configure-moderation-pin-comments-enabled">
            <Label component="legend">Enable Q&A</Label>
          </Localized>
          <Localized id="configure-moderation-pin-comments-enabled-description">
            <HelperText>
              When enabled, editors can pin comments for Q&A.
            </HelperText>
          </Localized>
        </FormFieldHeader>
        <OnOffField name="enableQaFlag" disabled={disabled} />

        {enableQaFlagInput.value === true && (
          <div style={{ marginTop: "1rem" }}>
            <Localized id="configure-moderation-pin-comments-sites">
              <Label>Select sites where Q&A is enabled</Label>
            </Localized>
            <HorizontalGutter spacing={3} mt={3} mb={3}>
              {(qaFlagEnabledSitesInput?.value || []).map((siteID: string) => {
                return (
                  <SpecificSitesSelectedQuery
                    key={siteID}
                    siteID={siteID}
                    onChange={onRemoveSite}
                  />
                );
              })}
            </HorizontalGutter>
            <SiteSearch
              showOnlyScopedSitesInSearchResults
              showAllSitesSearchFilterOption={false}
              showSiteSearchLabel={false}
              onSelect={onAddSite}
              clearTextFieldValueAfterSelect={true}
            />
          </div>
        )}
      </FormField>
    </ConfigBox>
  );
};

export default PinCommentsConfig;
